import React, { Component } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";

const EMPTY_FORM = {
  // A
  estimateNumber: "",
  // B
  workPerformedThrough: "",
  // C – Type of Estimate Requested (mutually exclusive)
  estimateRequestType: "",          // "monthly" | "afterAcceptance"
  // D1
  estimatedCompletionDate: "",
  // D2
  chargeableWorkingDays: "",
  nonworkingSuspensionDays: "",
  contractTimeExtSign: "-",
  contractTimeExtDays: "",
  // D3
  plantEstablishmentProgress: "",   // "satisfactory" | "notSatisfactory"
  // Unusual Progress Conditions
  overrideUnsatisfactory: false,
  percentTimeElapsed: "",
  unusualConditionsNote: "",
  // Estimate Type (mutually exclusive)
  estimateType: "",                 // "supplemental" | "proposedFinal" | "semifinal" | "final"
  // Rerun
  isRerun: false,
  // Deductions table (3 rows)
  deductions: [
    { type: "", description: "", sign: "-", amount: "" },
    { type: "", description: "", sign: "-", amount: "" },
    { type: "", description: "", sign: "-", amount: "" },
  ],
};

class EstimateDetailModal extends Component {
  state = { form: { ...EMPTY_FORM, deductions: EMPTY_FORM.deductions.map((d) => ({ ...d })) } };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      const init = this.props.initialValues;
      if (init && Object.keys(init).length > 0) {
        this.setState({ form: { ...EMPTY_FORM, ...init, deductions: init.deductions || EMPTY_FORM.deductions.map((d) => ({ ...d })) } });
      } else {
        this.setState({ form: { ...EMPTY_FORM, deductions: EMPTY_FORM.deductions.map((d) => ({ ...d })) } });
      }
    }
  }

  set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    this.setState((prev) => ({ form: { ...prev.form, [field]: value } }));
  };

  setDeduction = (index, field) => (e) => {
    const deductions = this.state.form.deductions.map((d, i) =>
      i === index ? { ...d, [field]: e.target.value } : d
    );
    this.setState((prev) => ({ form: { ...prev.form, deductions } }));
  };

  handleSave = () => {
    this.props.onSave(this.state.form);
  };

  render() {
    const { isOpen, onClose } = this.props;
    const { form } = this.state;

    const labelStyle = { fontWeight: 600, marginBottom: 0 };

    return (
      <Modal isOpen={isOpen} toggle={onClose} fade={false} size="lg" scrollable>
        <ModalHeader toggle={onClose}>
          Project Record &mdash; Estimate Request &nbsp;
          <small className="text-muted" style={{ fontSize: "0.75rem" }}>DOT CEM-6101 (REV 02/2025)</small>
        </ModalHeader>

        <ModalBody>

          {/* ── A. Estimate Number ───────────────────────────────────── */}
          <div className="mb-2 d-flex align-items-center">
            <label style={{ ...labelStyle, flex: 1, marginBottom: 0 }}>A.&nbsp; Estimate Number</label>
            <div style={{ width: "33%" }}>
              <Input bsSize="sm" value={form.estimateNumber} onChange={this.set("estimateNumber")} />
            </div>
          </div>

          {/* ── B. Estimate for Work Performed Through ───────────────── */}
          <div className="mb-2 d-flex align-items-center">
            <label style={{ ...labelStyle, flex: 1, marginBottom: 0 }}>B.&nbsp; Estimate for Work Performed Through</label>
            <div style={{ width: "33%" }}>
              <Input bsSize="sm" type="date" value={form.workPerformedThrough} onChange={this.set("workPerformedThrough")} />
            </div>
          </div>

          <hr className="my-2" />

          {/* ── C. Type of Estimate Requested ───────────────────────── */}
          <label style={labelStyle}>C.&nbsp; Type of Estimate Requested:</label>
          <div className="ml-2 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>1) Monthly Construction Progress Estimate</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="radio" name="estimateRequestType" value="monthly" style={{ margin: 0 }}
                checked={form.estimateRequestType === "monthly"}
                onChange={this.set("estimateRequestType")} />
            </div>
          </div>
          <div className="ml-2 mb-2 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>2) Progress Estimate After Acceptance</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="radio" name="estimateRequestType" value="afterAcceptance" style={{ margin: 0 }}
                checked={form.estimateRequestType === "afterAcceptance"}
                onChange={this.set("estimateRequestType")} />
            </div>
          </div>

          <hr className="my-2" />

          {/* ── D. Contract Progress ─────────────────────────────────── */}
          <label style={labelStyle}>D.&nbsp; Contract Progress:</label>

          {/* D1 */}
          <div className="ml-2 mb-2 d-flex align-items-center">
            <label style={{ ...labelStyle, flex: 1, marginBottom: 0 }}>1)&nbsp; Estimated Date of Completion (or Date of Completion)</label>
            <div style={{ width: "33%" }}>
              <Input bsSize="sm" type="date" value={form.estimatedCompletionDate} onChange={this.set("estimatedCompletionDate")} />
            </div>
          </div>

          {/* D2 */}
          <div className="ml-2 mb-1">
            <label style={labelStyle}>2)&nbsp; Chargeable Working Days</label>
          </div>
          <div className="ml-4 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>Chargeable Working Days</label>
            <div style={{ width: "33%" }}><Input bsSize="sm" type="number" value={form.chargeableWorkingDays} onChange={this.set("chargeableWorkingDays")} /></div>
          </div>
          <div className="ml-4 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>Nonworking Days + Suspension Days</label>
            <div style={{ width: "33%" }}><Input bsSize="sm" type="number" value={form.nonworkingSuspensionDays} onChange={this.set("nonworkingSuspensionDays")} /></div>
          </div>
          <div className="ml-4 mb-2 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>Contract Time Extension (contract change order)</label>
            <div style={{ width: "33%" }}>
              <InputGroup size="sm">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText style={{ padding: "0 4px" }}>
                    <Input type="select" bsSize="sm"
                      value={form.contractTimeExtSign}
                      onChange={this.set("contractTimeExtSign")}
                      style={{ border: "none", padding: 0, height: "auto", width: "42px" }}
                    >
                      <option value="+">+</option>
                      <option value="-">&minus;</option>
                    </Input>
                  </InputGroupText>
                </InputGroupAddon>
                <Input bsSize="sm" type="number" value={form.contractTimeExtDays} onChange={this.set("contractTimeExtDays")} />
              </InputGroup>
            </div>
          </div>

          {/* D3 */}
          <div className="ml-2 mb-1">
            <label style={labelStyle}>3)&nbsp; If on Plant Establishment Period:</label>
          </div>
          <div className="ml-4 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>Progress is satisfactory</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="checkbox" style={{ margin: 0 }}
                checked={form.plantEstablishmentProgress === "satisfactory"}
                onChange={() => this.setState((prev) => ({ form: { ...prev.form, plantEstablishmentProgress: prev.form.plantEstablishmentProgress === "satisfactory" ? "" : "satisfactory" } }))} />
            </div>
          </div>
          <div className="ml-4 mb-2 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>Progress is <b>not</b> satisfactory</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="checkbox" style={{ margin: 0 }}
                checked={form.plantEstablishmentProgress === "notSatisfactory"}
                onChange={() => this.setState((prev) => ({ form: { ...prev.form, plantEstablishmentProgress: prev.form.plantEstablishmentProgress === "notSatisfactory" ? "" : "notSatisfactory" } }))} />
            </div>
          </div>

          <hr className="my-2" />

          {/* ── Unusual Progress Conditions ──────────────────────────── */}
          <label style={labelStyle}>Unusual Progress Conditions:</label>

          {/* 1a */}
          <div className="ml-2 mb-2 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0, fontWeight: 600 }}>1a)&nbsp; Override Unsatisfactory Progress</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="checkbox" checked={form.overrideUnsatisfactory} onChange={this.set("overrideUnsatisfactory")} style={{ margin: 0 }} />
            </div>
          </div>

          {/* 1b */}
          <div className="ml-2 mb-2 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>1b)&nbsp; Percent Time Elapsed <b>will be</b> equal to</label>
            <div style={{ width: "33%" }}>
              <InputGroup size="sm">
                <Input type="number" min="0" max="100" value={form.percentTimeElapsed} onChange={this.set("percentTimeElapsed")} />
                <InputGroupAddon addonType="append">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </div>
          </div>

          {/* 1c */}
          <div className="ml-2 mb-3">
            <label style={{ marginBottom: "0.25rem", fontWeight: 600 }}>1c)&nbsp; Notes / Conditions</label>
            <Input type="textarea" rows={2} bsSize="sm" value={form.unusualConditionsNote} onChange={this.set("unusualConditionsNote")} />
          </div>

          {/* 2 – Estimate Type */}
          <div className="ml-2 mb-1">
            <label style={labelStyle}>2)&nbsp; Estimate Type</label>
          </div>
          <div className="ml-4 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>a) Supplemental Progress Estimate</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="radio" name="estimateType" value="supplemental" style={{ margin: 0 }}
                checked={form.estimateType === "supplemental"} onChange={this.set("estimateType")} />
            </div>
          </div>
          <div className="ml-4 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>b) Proposed Final Estimate</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="radio" name="estimateType" value="proposedFinal" style={{ margin: 0 }}
                checked={form.estimateType === "proposedFinal"} onChange={this.set("estimateType")} />
            </div>
          </div>
          <div className="ml-4 mb-1 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>c) Semifinal Estimate</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="radio" name="estimateType" value="semifinal" style={{ margin: 0 }}
                checked={form.estimateType === "semifinal"} onChange={this.set("estimateType")} />
            </div>
          </div>
          <div className="ml-4 mb-2 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0 }}>d) Final Estimate</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="radio" name="estimateType" value="final" style={{ margin: 0 }}
                checked={form.estimateType === "final"} onChange={this.set("estimateType")} />
            </div>
          </div>

          {/* 3 – Rerun */}
          <div className="ml-2 mb-3 d-flex align-items-center">
            <label style={{ flex: 1, marginBottom: 0, fontWeight: 600 }}>3)&nbsp; Indicate if this is a rerun of a prior estimate</label>
            <div style={{ width: "33%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Input type="checkbox" checked={form.isRerun} onChange={this.set("isRerun")} style={{ margin: 0 }} />
            </div>
          </div>

          <hr className="my-2" />

          {/* ── Deductions table ─────────────────────────────────────── */}
          <table className="table table-sm table-bordered w-100 mb-0">
            <thead className="thead-light">
              <tr>
                <th style={{ width: "12%" }}>TYPE</th>
                <th>DESCRIPTION</th>
                <th style={{ width: "48px", textAlign: "center" }}>±</th>
                <th style={{ width: "22%" }}>AMOUNT <small className="text-muted">(see note)</small></th>
              </tr>
            </thead>
            <tbody>
              {form.deductions.map((d, i) => (
                <tr key={i}>
                  <td>
                    <Input bsSize="sm" value={d.type} onChange={this.setDeduction(i, "type")} />
                  </td>
                  <td>
                    <Input bsSize="sm" value={d.description} onChange={this.setDeduction(i, "description")} />
                  </td>
                  <td>
                    <Input type="select" bsSize="sm" value={d.sign} onChange={this.setDeduction(i, "sign")}
                      style={{ textAlign: "center" }}>
                      <option value="-">&minus;</option>
                      <option value="+">+</option>
                    </Input>
                  </td>
                  <td>
                    <Input bsSize="sm" type="number" value={d.amount} onChange={this.setDeduction(i, "amount")} placeholder="0.00" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <small className="text-muted">
            <b>NOTE:</b> Use minus sign to take a deduction; plus sign indicates return of a previous deduction.
          </small>

        </ModalBody>

        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Cancel</Button>
          <Button color="primary" onClick={this.handleSave}>Save</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default EstimateDetailModal;
